import {
  Badge,
  type CountryCode,
  FlagComponent,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';

import type {
  OwnershipSummaryColumns,
  OwnershipSummaryOwnerRow,
  OwnershipSummaryTotals,
} from './OwnershipSummaryHelper';
import type { EntitySignatory } from './useEntitySignatories';

interface OwnershipSummaryOwnersTableProps {
  owners: OwnershipSummaryOwnerRow[];
  columns: OwnershipSummaryColumns;
  totals: OwnershipSummaryTotals;
  signatoriesByOwner: Record<string, EntitySignatory[]>;
}

export const OwnershipSummaryOwnersTable = ({
  owners,
  columns,
  totals,
  signatoriesByOwner,
}: OwnershipSummaryOwnersTableProps) => (
  <div className="overflow-hidden rounded-lg border border-border-secondary">
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>{columns.owner}</TableHead>
          <TableHead>Country</TableHead>
          {columns.holding ? (
            <TableHead className="text-right">{columns.holding}</TableHead>
          ) : null}
          <TableHead className="text-right">Ownership</TableHead>
          <TableHead className="text-right">{columns.capital}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {owners.map((owner) => (
          <TableRow key={owner.id} className="hover:bg-transparent">
            <TableCell>
              <div className="flex min-w-0 items-start gap-md">
                <span
                  aria-hidden
                  className={cn(
                    'mt-sm size-2 shrink-0 rounded-full',
                    owner.colorClassName
                  )}
                />
                <div className="flex min-w-0 flex-col gap-xxs">
                  <div className="flex flex-wrap items-center gap-sm">
                    <span className="txt-small-medium text-text-primary">
                      {owner.name}
                    </span>
                    <Badge variant="gray" size="sm">
                      {owner.kindLabel}
                    </Badge>
                    {owner.roleLabel ? (
                      <Badge
                        variant={owner.isLeadRole ? 'blue' : 'gray'}
                        size="sm"
                      >
                        {owner.roleLabel}
                      </Badge>
                    ) : null}
                  </div>
                  {owner.identityDetail ? (
                    <span className="txt-extra-small-regular text-text-tertiary">
                      {owner.identityDetail}
                    </span>
                  ) : null}
                  {signatoriesByOwner[owner.id]?.map((signatory) => (
                    <span
                      key={signatory.label}
                      className="txt-extra-small-regular text-text-secondary"
                    >
                      {signatory.label}:{' '}
                      <span className="txt-extra-small-medium text-text-primary">
                        {signatory.names.join(', ')}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </TableCell>
            <TableCell>
              {owner.country && owner.countryName ? (
                <div className="flex items-center gap-sm">
                  <FlagComponent
                    // Owner countries are ISO-3166 alpha-2 codes, the set CountryCode enumerates.
                    country={owner.country as CountryCode}
                    countryName={owner.countryName}
                    _className="size-4 rounded-full"
                  />
                  <span className="txt-small-regular text-text-primary">
                    {owner.countryName}
                  </span>
                </div>
              ) : (
                <span className="text-text-quaternary">—</span>
              )}
            </TableCell>
            {columns.holding ? (
              <TableCell className="txt-small-regular text-right text-text-primary">
                {owner.holding ?? '—'}
              </TableCell>
            ) : null}
            <TableCell className="txt-small-medium text-right text-text-primary">
              {owner.percentLabel}
            </TableCell>
            <TableCell className="txt-small-regular whitespace-nowrap text-right text-text-primary">
              {owner.capital}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow className="hover:bg-transparent">
          <TableCell className="txt-small-bold" colSpan={2}>
            Total
          </TableCell>
          {columns.holding ? (
            <TableCell className="txt-small-bold text-right">
              {totals.holding ?? '—'}
            </TableCell>
          ) : null}
          <TableCell className="txt-small-bold text-right">
            {totals.percentLabel}
          </TableCell>
          <TableCell className="txt-small-bold whitespace-nowrap text-right">
            {totals.capital}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  </div>
);
